import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Text,
  View,
  Pressable,
  useWindowDimensions,
} from "react-native";
import THEME from "../../../config/theme";
import { firestore } from "../../../services/firebase/index";
import Header from "../../../components/Header";
import {
  setDoc,
  addDoc,
  collection,
  getDocs,
  doc,
  orderBy,
  query,
  onSnapshot,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { useAuthUser } from "../../../contexts/useAuthUser";
import DataTable from "react-data-table-component";
import TouchableText from "../../../components/TouchableText";
import CreateProductModal from "./CreateProductModal";
import EditProductModal from "./EditProductModal";
import CreateStripePlanModal from "./stripe/CreateStripePlanModal";
import CreateStripeCourseModal from "./stripe/CreateStripeCourseModal";
import EditStripePlanModal from "./stripe/EditStripePlanModal";
import { Container, ViewDataTable, ContentIcon } from "./style";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";
import AdminSwitch from "../../../components/AdminSwitch";
import { Badge } from "./formStyles";
import { Provider } from "react-native-paper";
import { useNavigation, useRoute } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import Constants from "expo-constants";
import { useRemoteConfigContext } from "../../../contexts/useRemoteConfigContext";
import { getUsers } from "../../Users/user-service";
import { deleteDoc, where } from "firebase/firestore";
import { purgeCatalogEquivalentRefs, transferCatalogVitrineHeirship } from "../../../utils/syncCatalogEquivalentIds";
import { queryClient } from "../../../queries/queryClient";
import { fetchCatalogProducts } from "../../../queries/useCatalogProducts";
import {
  getCatalogVitrineHeirCandidates,
  needsCatalogVitrineHeirOnDelete,
} from "../../../utils/catalogVitrineReadiness";
import { CatalogVitrineHeirModal } from "../../../components/CatalogVitrineHeirModal";
import ProductCategoryAiAssistant from "../../../components/ProductCategoryAiAssistant";

const toJsDate = (v) =>
  v && typeof v.toDate === "function"
    ? v.toDate()
    : v instanceof Date
      ? v
      : null;

// Procura UM aluno com acesso ATIVO ao produto (Guru/Pagarme/IAP), parando na
// primeira ocorrência para minimizar leituras. Só é chamada quando o admin tenta
// EXCLUIR o produto — nunca no carregamento da tela. Varre os alunos em lotes e
// aborta assim que encontra o primeiro ativo. "Ativo": sem data = perpétuo;
// com data = futura (mesma regra da lista de alunos).
const hasActiveStudentForProduct = async (productId) => {
  if (!productId) return false;
  const now = new Date();
  let lastVisible = null;
  const BATCH = 1000;

  while (true) {
    const snap = await getUsers({
      pageReference: lastVisible,
      pageSize: BATCH,
      isNextPage: true,
    });
    if (!snap || snap.empty) break;

    for (const d of snap.docs) {
      const u = d.data();
      const activeProduct =
        Array.isArray(u.productIds) &&
        u.productIds.some((p) => {
          if (p?.productId !== productId) return false;
          const e = toJsDate(p.expirationDate);
          return !e || e > now;
        });
      if (activeProduct) return true; // parada antecipada
    }

    if (snap.docs.length < BATCH) break;
    lastVisible = snap.docs[snap.docs.length - 1];
  }
  return false;
};

// Botão primário padrão do admin (verde-água + texto branco + ícone), igual às
// demais telas. Definido em escopo de módulo p/ não remontar a cada render.
const NewButton = ({ label, onPress }) => (
  <Pressable
    onPress={onPress}
    style={({ hovered, pressed }) => ({
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 10,
      backgroundColor:
        hovered || pressed ? ADMIN_COLORS.accentDark : ADMIN_COLORS.teal,
      boxShadow: "0 1px 2px rgba(16,24,40,0.08)",
      cursor: "pointer",
    })}
  >
    <Feather name="plus" size={16} color="#FFFFFF" />
    <Text
      style={{
        color: "#FFFFFF",
        fontFamily: ADMIN_FONT,
        fontSize: 14,
        fontWeight: "600",
      }}
    >
      {label}
    </Text>
  </Pressable>
);

// Estado vazio (substitui o "There are no records to display" do DataTable).
const EmptyState = ({ text }) => (
  <View
    style={{
      paddingVertical: 36,
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
    }}
  >
    <Feather name="inbox" size={24} color={ADMIN_COLORS.placeholder} />
    <Text
      style={{
        marginTop: 8,
        fontFamily: ADMIN_FONT,
        fontSize: 13,
        color: ADMIN_COLORS.muted,
      }}
    >
      {text}
    </Text>
  </View>
);

export function Products({}) {
  const navigation = useNavigation();
  const route = useRoute();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const titleFontSize = isMobile ? 24 : 32;
  const [products, setProducts] = useState([]);
  const [iapProducts, setIapProducts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [courses, setCourses] = useState([]);
  const { user } = useAuthUser();
  const [isCreateProductModalVisible, setIsCreateProductModalVisible] =
    useState(false);
  const [isEditProductModalVisible, setIsEditProductModalVisible] =
    useState(false);
  const [isCreateStripePlanModalVisible, setIsCreateStripePlanModalVisible] =
    useState(false);
  const [
    isCreateStripeCourseModalVisible,
    setIsCreateStripeCourseModalVisible,
  ] = useState(false);
  const [isEditStripePlanModalVisible, setIsEditStripePlanModalVisible] =
    useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [heirModalVisible, setHeirModalVisible] = useState(false);
  const [pendingDeleteTarget, setPendingDeleteTarget] = useState(null);
  const [heirCandidates, setHeirCandidates] = useState([]);

  const config = useRemoteConfigContext();
  const { guruUserToken } = Constants.manifest.extra;

  const handleOpenCreateProductModal = () => {
    setIsCreateProductModalVisible(true);
  };

  const handleCloseCreateProductModal = () => {
    setIsCreateProductModalVisible(false);
  };

  const handleOpenEditProductModal = (row) => {
    setSelectedProduct(row);
    setIsEditProductModalVisible(true);
  };

  const handleCloseEditProductModal = () => {
    setIsEditProductModalVisible(false);
  };

  // Reabre o modal do produto quando voltamos da tela "Editar Ofertas"
  // (ProductConfig envia o produto em route.params.reopenProduct ao clicar em Voltar).
  useEffect(() => {
    const reopenProduct = route.params?.reopenProduct;
    if (reopenProduct) {
      handleOpenEditProductModal(reopenProduct);
      navigation.setParams({ reopenProduct: undefined });
    }
  }, [route.params?.reopenProduct]);

  const handleOpenCreateStripePlanModal = () => {
    setIsCreateStripePlanModalVisible(true);
  };

  const handleCloseCreateStripePlanModal = () => {
    setIsCreateStripePlanModalVisible(false);
  };

  const handleOpenCreateStripeCourseModal = () => {
    setSelectedCourse(null);
    setIsCreateStripeCourseModalVisible(true);
  };

  const handleCloseCreateStripeCourseModal = () => {
    setIsCreateStripeCourseModalVisible(false);
    setSelectedCourse(null);
  };

  const handleOpenEditStripeCourseModal = (row) => {
    setSelectedCourse(row);
    setIsCreateStripeCourseModalVisible(true);
  };

  const handleOpenEditStripePlanModal = (row) => {
    setSelectedPlan(row);
    setIsEditStripePlanModalVisible(true);
  };

  const handleCloseEditStripePlanModal = () => {
    setIsEditStripePlanModalVisible(false);
  };

  const handleCreateProduct = async (productInfo, offersInfos, productId) => {
    try {
      // Verificar se o produto já existe
      const productExists = products.some(
        (product) => product.productId === productId,
      );
      if (productExists) {
        return { error: "Você já possui este produto salvo na lista" };
      }

      const productRef = await addDoc(collection(firestore, "products"), {
        name: productInfo.name,
        type: productInfo.type,
        productId: productId,
        marketplace: "Guru Digital",
        marketplaceName:
          productInfo.marketplace_name || productInfo.marketplaceName || null,
        archived: true,
      });

      // Crie documentos na subcoleção 'offers' com as informações de cada oferta
      for (const offerInfo of offersInfos.data) {
        await addDoc(collection(productRef, "offers"), offerInfo);
      }
      return { success: true };
    } catch (error) {
      console.error("Erro ao criar produto:", error);
      return { error: "Erro ao criar produto" };
    }
  };

  const handleEditProduct = async (
    productInfo,
    offersInfos,
    hasExpirationDate,
    expirationDays,
    checkoutUrl,
    archived,
    description,
    selectedProduct,
    expirationType,
    fixedExpirationDate,
  ) => {
    const productRef = doc(firestore, "products", selectedProduct?.id);
    try {
      const isFixedDate = expirationType === "fixedDate";

      const updateData = {
        marketplace: "Guru Digital",
        name: productInfo?.name,
        productId: productInfo?.marketplace_id,
        type: productInfo?.type,
        expirationType: isFixedDate ? "fixedDate" : "days",
        fixedExpirationDate: isFixedDate ? fixedExpirationDate || null : null,
      };

      if (productInfo?.marketplace_name || productInfo?.marketplaceName) {
        updateData.marketplaceName =
          productInfo.marketplace_name || productInfo.marketplaceName;
      }

      if (isFixedDate) {
        // Fixed-date mode implies "has an expiration"; legacy day field is nulled.
        updateData.hasExpirationDate = true;
        updateData.days = null;
      } else {
        if (hasExpirationDate !== undefined) {
          updateData.hasExpirationDate = hasExpirationDate;
        }
        if (expirationDays !== undefined) {
          updateData.days = expirationDays;
        }
      }
      if (checkoutUrl !== undefined) {
        updateData.checkoutUrl = checkoutUrl;
      }
      if (archived !== undefined) {
        updateData.archived = archived;
      }
      if (description !== undefined) {
        updateData.description = description;
      }

      await setDoc(productRef, updateData, { merge: true });

      const offersCollectionRef = collection(productRef, "offers");
      const offersSnapshot = await getDocs(offersCollectionRef);

      for (const offerInfo of offersInfos.data) {
        const offerId = offerInfo.id;
        const existingOfferDoc = offersSnapshot.docs.find(
          (doc) => doc.data().id === offerId,
        );
        if (existingOfferDoc) {
          await setDoc(
            doc(offersCollectionRef, existingOfferDoc.id),
            offerInfo,
            { merge: true },
          );
        } else {
          await addDoc(offersCollectionRef, offerInfo);
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar documento:", error);
    }
  };

  const findAllProducts = async () => {
    const productsRef = collection(firestore, `products`);
    const q = query(productsRef, orderBy("name"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedProducts = [];
      snapshot.forEach((doc) => {
        const data = doc.data();

        if (
          data?.marketplace === "Guru Digital" ||
          data?.marketplace === "PAGARME"
        ) {
          updatedProducts.push({ id: doc.id, ...data });
        }
        // IAP é carregado à parte (findAllIAPProducts), pois deve aparecer
        // para o cliente independentemente do gateway web (Guru/Stripe/Pagarme).
      });
      setProducts(updatedProducts);
      setLoading(false);
    });
    return unsubscribe;
  };

  // Compras In-App (Apple Store / Google Play) — sempre carregadas, qualquer que
  // seja o gateway web. IAP usa o campo `active` (boolean) em vez de `archived`;
  // normalizamos para reaproveitar a lógica de status/estilos. O tipo é deduzido
  // pela recorrência no productId (assinaturas começam com "iap." e têm frequência).
  const findAllIAPProducts = () => {
    const productsRef = collection(firestore, "products");
    const q = query(productsRef, where("marketplace", "==", "IAP"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          list.push({
            id: doc.id,
            ...data,
            archived: data.active === false,
            type:
              data.type ||
              (/monthly|quarterly|semiannual|yearly/i.test(data.productId || "")
                ? "plan"
                : "product"),
          });
        });
        setIapProducts(list);
        setLoading(false);
      },
      (error) => {
        console.warn("Falha ao carregar produtos IAP:", error);
        setIapProducts([]);
        setLoading(false);
      },
    );
    return unsubscribe;
  };

  const findAllStripePlans = async () => {
    const plansRef = collection(firestore, `plans`);
    const q = query(plansRef, orderBy("order", "asc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const updatedPlans = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filtra apenas planos (não pega o documento de frequência)
          if (data.isPlanFrequency === false) {
            updatedPlans.push({ id: doc.id, ...data });
          }
        });
        setPlans(updatedPlans);
        setLoading(false);
      },
      (error) => {
        console.warn("Falha ao carregar plans:", error);
        setPlans([]);
        setLoading(false);
      },
    );
    return unsubscribe;
  };

  const findAllStripeCourses = async () => {
    const coursesRef = collection(firestore, `courses`);
    const unsubscribe = onSnapshot(
      coursesRef,
      (snapshot) => {
        const updatedCourses = [];
        snapshot.forEach((doc) => {
          updatedCourses.push({ id: doc.id, ...doc.data() });
        });
        setCourses(updatedCourses);
        setLoading(false);
      },
      (error) => {
        console.warn("Falha ao carregar courses:", error);
        setCourses([]);
        setLoading(false);
      },
    );
    return unsubscribe;
  };

  // Exclusão de produto (Guru/Pagarme). Só permite excluir se NÃO houver
  // nenhum aluno ativo no produto — a busca (early-exit) só é disparada aqui,
  // quando o admin realmente pede a exclusão. Confirmação e feedback via alert.
  const findCatalogProduct = (target, products = catalogProducts) =>
    products.find((item) => item.id === target?.id && item.type === "product");

  const executeDeleteProduct = async (target, heirCatalogProduct = null) => {
    if (!target?.id) return;

    setIsDeletingProduct(true);
    try {
      const freshCatalogProducts = await fetchCatalogProducts({
        guruUserToken,
        paymentGateway: config.paymentGateway,
      });
      const catalogProduct = findCatalogProduct(target, freshCatalogProducts);

      if (heirCatalogProduct && catalogProduct) {
        const heirProduct = freshCatalogProducts.find((item) => item.id === heirCatalogProduct.id);
        if (heirProduct) {
          await transferCatalogVitrineHeirship({
            deletedProduct: catalogProduct,
            heirProduct,
            catalogProducts: freshCatalogProducts,
          });
        }
      }

      await purgeCatalogEquivalentRefs({ id: target.id, type: "product" });

      const productRef = doc(firestore, "products", target.id);
      const offersSnap = await getDocs(collection(productRef, "offers"));
      for (const offerDoc of offersSnap.docs) {
        await deleteDoc(offerDoc.ref);
      }
      await deleteDoc(productRef);
      queryClient.invalidateQueries({ queryKey: ["catalogProducts"] });

      setHeirModalVisible(false);
      setPendingDeleteTarget(null);
      setHeirCandidates([]);
      handleCloseEditProductModal();
    } catch (error) {
      console.error("Erro ao excluir produto:", error);
      window.alert("Erro ao excluir o produto. Tente novamente.");
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    const target = product || selectedProduct;
    if (!target?.id) return;

    try {
      const hasActive = await hasActiveStudentForProduct(target.productId);
      if (hasActive) {
        window.alert(
          "Não é possível excluir: existe pelo menos um aluno com acesso ativo a este produto. " +
            "Remova/expire os acessos ativos antes de excluir.",
        );
        return;
      }

      const freshCatalogProducts = await fetchCatalogProducts({
        guruUserToken,
        paymentGateway: config.paymentGateway,
      });
      const catalogProduct = findCatalogProduct(target, freshCatalogProducts);

      if (
        needsCatalogVitrineHeirOnDelete(
          catalogProduct,
          freshCatalogProducts,
          config.hasProductCatalog
        )
      ) {
        setPendingDeleteTarget(target);
        setHeirCandidates(
          getCatalogVitrineHeirCandidates(catalogProduct, freshCatalogProducts)
        );
        setHeirModalVisible(true);
        return;
      }

      const confirmed = window.confirm(
        `Excluir definitivamente o produto "${target.name || target.productId}"? Esta ação não pode ser desfeita.`,
      );
      if (!confirmed) return;

      await executeDeleteProduct(target);
    } catch (error) {
      console.error("Erro ao verificar exclusão do produto:", error);
      window.alert("Erro ao preparar a exclusão. Tente novamente.");
    }
  };

  const handleConfirmHeirDelete = async (heirCatalogProduct) => {
    if (!pendingDeleteTarget) return;
    await executeDeleteProduct(pendingDeleteTarget, heirCatalogProduct);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (guruUserToken || config.paymentGateway === "PAGARME") {
        await findAllProducts();
      } else {
        await findAllStripePlans();
        await findAllStripeCourses();
      }
      // IAP aparece para todos os gateways (mobile pode ter compras In-App
      // mesmo em tenants Guru/Stripe/Pagarme).
      findAllIAPProducts();
    };
    fetchData();
  }, [config.paymentGateway]);

  const handleToggleCourseStatus = async (row, nextIsActive) => {
    try {
      await updateDoc(doc(firestore, "courses", row.id), {
        archived: nextIsActive ? false : true,
      });
    } catch (error) {
      console.error("Erro ao atualizar status do curso:", error);
    }
  };

  const handleToggleShowInCatalog = async (row, collectionName, nextVisible, event) => {
    event?.stopPropagation?.();
    try {
      await updateDoc(doc(firestore, collectionName, row.id), {
        showInCatalog: nextVisible,
      });
      queryClient.invalidateQueries({ queryKey: ["catalogProducts"] });
    } catch (error) {
      console.error("Erro ao atualizar exibição na vitrine:", error);
    }
  };

  const buildVitrineColumn = (collectionName) => ({
    name: "Vitrine",
    maxWidth: "10%",
    cell: (row, _rowIndex, _column, id) => (
      <View
        onStartShouldSetResponder={() => true}
        onTouchEnd={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <AdminSwitch
          value={row.showInCatalog !== false}
          onValueChange={(value) =>
            handleToggleShowInCatalog(row, collectionName, value)
          }
        />
      </View>
    ),
    ignoreRowClick: true,
  });

  if (loading)
    return (
      <ActivityIndicator
        style={{
          flex: 1,
          backgroundColor: ADMIN_COLORS.surface,
          justifyContent: "center",
        }}
        color={ADMIN_COLORS.teal}
        size="large"
      />
    );

  const conditionalRowStyles = [
    {
      when: (row) => row.archived !== false,
      style: {
        backgroundColor: ADMIN_COLORS.dangerSoft, // Vermelho se archived for verdadeiro
      },
    },
    {
      when: (row) => row.archived === false,
      style: {
        backgroundColor: ADMIN_COLORS.surface, // Verde se archived for falso
      },
    },
  ];

  // Colunas para produtos (Guru/Pagarme)
  const productColumns = [
    {
      name: "Nome do Produto",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Descrição",
      selector: (row) => row.description || "-",
      sortable: true,
      maxWidth: "50%",
    },
    {
      name: "Código",
      selector: (row) => row.productId,
      sortable: true,
      maxWidth: "8%",
    },
    {
      name: "Marketplace",
      selector: (row) => row.marketplace,
      sortable: true,
      maxWidth: "10%",
      cell: (row) => {
        const label =
          row.marketplace === "Guru Digital"
            ? "Guru"
            : row.marketplace === "PAGARME"
              ? "Pagarme"
              : row.marketplace === "IAP"
                ? "IAP"
                : row.marketplace || "-";
        return <Badge variant="neutral">{label}</Badge>;
      },
    },
    {
      name: "Tipo",
      selector: (row) =>
        row.type == "plan"
          ? "assinatura"
          : row.type == "product"
            ? "produto"
            : row.type,
      sortable: true,
      maxWidth: "8%",
      cell: (row) =>
        row.type === "plan" ? (
          <Badge variant="subscription">Assinatura</Badge>
        ) : row.type === "product" ? (
          <Badge variant="product">Produto</Badge>
        ) : (
          <Badge variant="neutral">{row.type || "-"}</Badge>
        ),
    },
    {
      name: "Status",
      selector: (row) => (row.archived == true ? "Inativo" : "Ativo"),
      sortable: true,
      maxWidth: "8%",
      cell: (row) => (
        <Badge variant={row.archived === false ? "success" : "neutral"}>
          {row.archived === false ? "Ativo" : "Inativo"}
        </Badge>
      ),
    },
  ];

  // Colunas para planos Stripe
  const stripePlanColumns = [
    {
      name: "Nome do Plano",
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: "Mensal (R$)",
      selector: (row) => row.monthlyPrice || "-",
      sortable: true,
    },
    {
      name: "Trimestral (R$)",
      selector: (row) => row.quarterlyPrice || "-",
      sortable: true,
    },
    {
      name: "Semestral (R$)",
      selector: (row) => row.semiannualPrice || "-",
      sortable: true,
    },
    {
      name: "Anual (R$)",
      selector: (row) => row.yearlyPrice || "-",
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => (row.archived == true ? "Inativo" : "Ativo"),
      sortable: true,
    },
  ];

  const stripeCourseColumns = [
    {
      name: "Nome do Curso",
      selector: (row) => row.titleCourse || row.title || row.name || "-",
      sortable: true,
    },
    {
      name: "Pagamento",
      selector: () => "Lifetime/Paga 1 vez",
      sortable: false,
    },
    {
      name: "Valor (R$)",
      selector: (row) =>
        row.price || row.valueCourseWithRS || row.priceCourse || "-",
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => {
        const isActive = row.archived === false;
        return (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Text>{isActive ? "Ativo" : "Inativo"}</Text>
            <AdminSwitch
              value={isActive}
              onValueChange={(value) => handleToggleCourseStatus(row, value)}
              accent={ADMIN_COLORS.success}
            />
          </View>
        );
      },
    },
  ];

  const vitrineColumnProducts = config.hasProductCatalog
    ? buildVitrineColumn("products")
    : null;
  const vitrineColumnPlans = config.hasProductCatalog
    ? buildVitrineColumn("plans")
    : null;
  const vitrineColumnCourses = config.hasProductCatalog
    ? buildVitrineColumn("courses")
    : null;

  const guruProductColumns = vitrineColumnProducts
    ? [...productColumns, vitrineColumnProducts]
    : productColumns;
  const stripePlanColumnsWithVitrine = vitrineColumnPlans
    ? [...stripePlanColumns, vitrineColumnPlans]
    : stripePlanColumns;
  const stripeCourseColumnsWithVitrine = vitrineColumnCourses
    ? [...stripeCourseColumns, vitrineColumnCourses]
    : stripeCourseColumns;

  // Produtos ordenados: ativos primeiro, inativos depois; desempate por nome.
  const sortedProducts = [...products].sort((a, b) => {
    const aInactive = a.archived === false ? 0 : 1;
    const bInactive = b.archived === false ? 0 : 1;
    if (aInactive !== bInactive) return aInactive - bInactive;
    return (a.name || "").localeCompare(b.name || "", "pt-BR");
  });

  const sortedPlans = [...plans].sort((a, b) => {
    const nameA = (a.title || "").toString();
    const nameB = (b.title || "").toString();
    return nameA.localeCompare(nameB, "pt-BR");
  });

  const sortedCourses = [...courses].sort((a, b) => {
    const nameA = (a.title || a.titleCourse || a.name || "").toString();
    const nameB = (b.title || b.titleCourse || b.name || "").toString();
    return nameA.localeCompare(nameB, "pt-BR");
  });

  const sortedIapProducts = [...iapProducts].sort((a, b) => {
    const aInactive = a.archived === false ? 0 : 1;
    const bInactive = b.archived === false ? 0 : 1;
    if (aInactive !== bInactive) return aInactive - bInactive;
    return (a.name || a.productId || "").localeCompare(
      b.name || b.productId || "",
      "pt-BR",
    );
  });

  return (
    <Provider>
      <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
        <Header navigation={navigation} user={user} isAdminPage />
        <Container style={{ flex: 1 }}>
          {/* Modais para Guru/Pagarme */}
          {guruUserToken || config.paymentGateway === "PAGARME" ? (
            <>
              {isCreateProductModalVisible && (
                <CreateProductModal
                  isVisible={isCreateProductModalVisible}
                  onClose={() => handleCloseCreateProductModal()}
                  onCreate={handleCreateProduct}
                />
              )}
              {isEditProductModalVisible && (
                <EditProductModal
                  key={selectedProduct?.id}
                  isVisible={isEditProductModalVisible}
                  onClose={() => handleCloseEditProductModal()}
                  onEdit={handleEditProduct}
                  onDelete={handleDeleteProduct}
                  isDeleting={isDeletingProduct}
                  selectedProduct={selectedProduct}
                />
              )}
              <CatalogVitrineHeirModal
                isVisible={heirModalVisible}
                deletedProductName={pendingDeleteTarget?.name || pendingDeleteTarget?.productId}
                candidates={heirCandidates}
                onCancel={() => {
                  if (isDeletingProduct) return;
                  setHeirModalVisible(false);
                  setPendingDeleteTarget(null);
                  setHeirCandidates([]);
                }}
                onConfirm={handleConfirmHeirDelete}
                isSubmitting={isDeletingProduct}
              />
            </>
          ) : (
            //Modais para Stripe
            <>
              {isCreateStripePlanModalVisible && (
                <CreateStripePlanModal
                  isVisible={isCreateStripePlanModalVisible}
                  onClose={() => handleCloseCreateStripePlanModal()}
                  onCreate={findAllStripePlans}
                  plansInfo={plans}
                  user={user}
                />
              )}
              {isCreateStripeCourseModalVisible && (
                <CreateStripeCourseModal
                  isVisible={isCreateStripeCourseModalVisible}
                  onClose={() => handleCloseCreateStripeCourseModal()}
                  onCreate={findAllStripeCourses}
                  plansInfo={plans}
                  user={user}
                  selectedCourse={selectedCourse}
                />
              )}
              {isEditStripePlanModalVisible && (
                <EditStripePlanModal
                  isVisible={isEditStripePlanModalVisible}
                  onClose={() => handleCloseEditStripePlanModal()}
                  onEdit={findAllStripePlans}
                  selectedPlan={selectedPlan}
                  user={user}
                />
              )}
              {/* Tenant Stripe também pode ter IAP: o editor (só vínculo de
                            categorias/módulos) precisa estar disponível aqui. */}
              {isEditProductModalVisible &&
                selectedProduct?.marketplace === "IAP" && (
                  <EditProductModal
                    key={selectedProduct?.id}
                    isVisible={isEditProductModalVisible}
                    onClose={() => handleCloseEditProductModal()}
                    onEdit={handleEditProduct}
                    selectedProduct={selectedProduct}
                  />
                )}
            </>
          )}

          {/* DataTable - muda conforme gateway */}
          <ViewDataTable style={{ paddingHorizontal: isMobile ? 12 : 30 }}>
            {config.hasProductCatalog ? <ProductCategoryAiAssistant /> : null}
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 16,
                marginBottom: 16,
              }}
            >
              <Text
                style={{
                  fontSize: titleFontSize,
                  fontFamily: ADMIN_FONT,
                  fontWeight: "700",
                  color: ADMIN_COLORS.ink,
                }}
              >
                {guruUserToken || config.paymentGateway === "PAGARME"
                  ? "Produtos"
                  : "Planos"}
              </Text>
              {!(guruUserToken || config.paymentGateway === "PAGARME") && (
                <NewButton
                  label="Novo Plano"
                  onPress={handleOpenCreateStripePlanModal}
                />
              )}
            </View>
            <DataTable
              style={{
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${ADMIN_COLORS.border}`,
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
              }}
              columns={
                guruUserToken || config.paymentGateway === "PAGARME"
                  ? guruProductColumns
                  : stripePlanColumnsWithVitrine
              }
              data={
                guruUserToken || config.paymentGateway === "PAGARME"
                  ? sortedProducts
                  : sortedPlans
              }
              noDataComponent={
                <EmptyState
                  text={
                    guruUserToken || config.paymentGateway === "PAGARME"
                      ? "Nenhum produto cadastrado"
                      : "Nenhum plano cadastrado"
                  }
                />
              }
              highlightOnHover
              pointerOnHover
              striped={false}
              customStyles={customStyles}
              onRowClicked={(row, event) =>
                guruUserToken || config.paymentGateway === "PAGARME"
                  ? // Produtos IAP são somente leitura aqui (não abrem o editor Guru).
                    row.marketplace === "IAP"
                    ? null
                    : handleOpenEditProductModal(row)
                  : handleOpenEditStripePlanModal(row)
              }
              conditionalRowStyles={conditionalRowStyles}
            />

            {!(guruUserToken || config.paymentGateway === "PAGARME") && (
              <>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 32,
                    marginBottom: 16,
                  }}
                >
                  <Text
                    style={{
                      fontSize: titleFontSize,
                      fontFamily: ADMIN_FONT,
                      fontWeight: "700",
                      color: ADMIN_COLORS.ink,
                    }}
                  >
                    Cursos
                  </Text>
                  <NewButton
                    label="Novo Curso"
                    onPress={handleOpenCreateStripeCourseModal}
                  />
                </View>
                <DataTable
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    border: `1px solid ${ADMIN_COLORS.border}`,
                    boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                  }}
                  columns={stripeCourseColumnsWithVitrine}
                  data={sortedCourses}
                  noDataComponent={
                    <EmptyState text="Nenhum curso cadastrado" />
                  }
                  highlightOnHover
                  pointerOnHover
                  striped={false}
                  customStyles={customStyles}
                  onRowClicked={(row) => handleOpenEditStripeCourseModal(row)}
                  conditionalRowStyles={conditionalRowStyles}
                />
              </>
            )}

            {/* Assinaturas In-App (IAP) — aparecem em qualquer gateway.
                        Clicar abre o editor para vincular categorias/módulos (permissões). */}
            {sortedIapProducts.length > 0 && (
              <>
                <View
                  style={{
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 12,
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 32,
                    marginBottom: 16,
                  }}
                >
                  <Text
                    style={{
                      fontSize: titleFontSize,
                      fontFamily: ADMIN_FONT,
                      fontWeight: "700",
                      color: ADMIN_COLORS.ink,
                    }}
                  >
                    Assinaturas do aplicativo (In-App)
                  </Text>
                </View>
                <DataTable
                  style={{
                    borderRadius: 12,
                    overflow: "hidden",
                    border: `1px solid ${ADMIN_COLORS.border}`,
                    boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                  }}
                  columns={guruProductColumns}
                  data={sortedIapProducts}
                  noDataComponent={
                    <EmptyState text="Nenhuma assinatura In-App" />
                  }
                  highlightOnHover
                  pointerOnHover
                  striped={false}
                  customStyles={customStyles}
                  onRowClicked={(row) => handleOpenEditProductModal(row)}
                  conditionalRowStyles={conditionalRowStyles}
                />
              </>
            )}
          </ViewDataTable>
        </Container>

        {/* FAB fixo no canto inferior direito (fora do ScrollView p/ não rolar) */}
        {(guruUserToken || config.paymentGateway === "PAGARME") &&
          !isCreateProductModalVisible && (
            <ContentIcon>
              <TouchableText
                onPress={handleOpenCreateProductModal}
                title="+"
                color={ADMIN_COLORS.surface}
                fontFamily={THEME.FONTFAMILY.BOLD}
                fontSize={THEME.FONTSIZE.BIG}
              ></TouchableText>
            </ContentIcon>
          )}
      </View>
    </Provider>
  );
}

const customStyles = {
  table: {
    style: {
      backgroundColor: ADMIN_COLORS.surface,
    },
  },
  headRow: {
    style: {
      backgroundColor: ADMIN_COLORS.surfaceSubtle,
      borderBottomWidth: "1px",
      borderBottomColor: ADMIN_COLORS.border,
      minHeight: "48px",
    },
  },
  headCells: {
    style: {
      color: ADMIN_COLORS.muted,
      fontFamily: ADMIN_FONT,
      fontSize: "12px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.04em",
    },
  },
  rows: {
    style: {
      color: ADMIN_COLORS.ink,
      fontFamily: ADMIN_FONT,
      fontSize: "14px",
      minHeight: "56px",
      borderBottomColor: ADMIN_COLORS.border,
    },
    highlightOnHoverStyle: {
      backgroundColor: ADMIN_COLORS.surfaceSubtle,
      borderBottomColor: ADMIN_COLORS.border,
      outline: "none",
    },
  },
  cells: {
    style: {
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
};
